import { useEffect, useState } from 'react'
import { Modal, ModalBody, Button, Row, Col, ModalHeader, ModalFooter, Alert } from 'reactstrap'
import { collection, getDocs, query, updateDoc, doc } from "firebase/firestore"
import { db, addPrefix } from '../firebase'
import { createApolloClient } from '../ApolloClient'
import { getBaseURL } from '../functions'
import Select from 'react-select'
import { groupedDropdownCustomStyling } from '../constants'

function TenantBranchSelectionModal({ open, handleClose, handleSetParam, currentUser }) {
    const [allBranches, setAllBranches] = useState([])
    const [branchOptions, setBranchOptions] = useState([])
    const [selectedBranchOption, setSelectedBranchOption] = useState(null)
    const [error, setError] = useState("")

    // Load branches on component mount
    useEffect(() => {
        loadAllBranches()
    }, []) // Empty dependency array since we only want to load branches once

    // Handle branch selection when currentUser or allBranches changes
    useEffect(() => {
        if (currentUser?.selectedBranch && allBranches.length > 0) {
            const currentBranch = allBranches.find(item => item.customId === currentUser.selectedBranch)
            if (currentBranch) {
                const currentOption = {
                    value: `${currentBranch.tenant}-${currentBranch.customId}`,
                    label: currentBranch.name,
                    tenant: currentBranch.tenant,
                    branchId: currentBranch.customId,
                    branchData: currentBranch
                }
                setSelectedBranchOption(currentOption)
            }
        }
    }, [currentUser?.selectedBranch, allBranches])

    const loadAllBranches = async () => {
        const querySnapshot = await getDocs(query(collection(db, addPrefix("branches"))))
        const branches = querySnapshot.docs.map((doc) => ({ ...doc.data(), firebaseId: doc.id }))
        setAllBranches(branches)

        // Extract unique tenants from branches and sort them
        let tenants = [...new Set(branches.map(branch => branch.tenant))].filter(Boolean).sort((a, b) => a.localeCompare(b))

        // If path is checkin, remove 'Other' tenant
        if (window.location.pathname.includes("checkin")) {
            tenants = tenants.filter(tenant => tenant.toLowerCase() !== "other")
        }

        // Group branches by tenant
        const grouped = {}
        tenants.forEach(tenantName => {
            grouped[tenantName] = branches.filter(branch => branch.tenant === tenantName)
        })

        // Create options for react-select with groups (tenants and branches sorted by name)
        const options = tenants.map(tenantName => ({
            label: tenantName,
            options: grouped[tenantName]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(branchItem => ({
                    value: `${tenantName}-${branchItem.customId}`,
                    label: branchItem.name,
                    tenant: tenantName,
                    branchId: branchItem.customId,
                    branchData: branchItem
                }))
        }))
        setBranchOptions(options)
    }

    const handleQueueSelection = async () => {
        if (selectedBranchOption) {
            // Save selected branch to user's document in Firebase
            if (currentUser && currentUser.pk) {
                try {
                    await updateDoc(doc(db, addPrefix("users"), String(currentUser.pk)), {
                        selectedBranch: selectedBranchOption.branchId,
                    })
                } catch (err) {
                    console.error("Error saving branch selection:", err)
                }
            }

            handleSetParam(selectedBranchOption.branchId)
            if (!window.location.hostname.includes("localhost") && !window.location.hostname.includes("staging"))
                createApolloClient(getBaseURL(selectedBranchOption.tenant))
            handleClose()
        } else {
            setError("Please select at-least one queue")
        }
    }

    return (
        <Modal isOpen={open} className="vms-ticket-modal" style={{ width: "30%" }}>
            <ModalHeader>Select Queue</ModalHeader>
            <ModalBody className="px-4">
                {error && <Row><Col xs={12}><Alert color="danger">{error}</Alert></Col></Row>}
                <Row>
                    <Col>
                        <div className="mb-3">
                            <label className="form-label">Select Queue *</label>
                            <Select
                                value={selectedBranchOption}
                                onChange={setSelectedBranchOption}
                                options={branchOptions}
                                placeholder="Select a queue..."
                                isSearchable={true}
                                isClearable={false}
                                styles={groupedDropdownCustomStyling}
                                className="branch-select-modal"
                                classNamePrefix="branch-select-modal"
                            />
                        </div>
                    </Col>
                </Row>
            </ModalBody>
            <ModalFooter>
                <Button className="btn" color="primary" onClick={handleQueueSelection}>Set Queue</Button>
            </ModalFooter>
        </Modal>
    )
}

export default TenantBranchSelectionModal
